import * as ReasonsActions from '../../../ng2/main/reasons/reasons.actions.ts';
import { authenticatedPatientSelector, currentPatientSelector } from '../../selectors/patients-selectors.ts';

module.exports = {
  controller: class ReasonPreferences {

    static $inject = [
      '$ngRedux',
      '$scope',
      '$state',
      'LoadingService',
      'ProductService',
      'ReasonsService',
    ];

    models = {
      choice: '',
      note: '',
      availability: {
        eligibleAccounts: [],
        soonestPossibleDate: undefined,
      },
    };

    reasons = [];

    appointmentMomentPreferences = {
      type: 'asap',
      momentOfDay: {
        morning: true,
        afternoon: true,
        evening: true,
      },
      date: moment(),
    };

    constructor(
      $ngRedux,
      $scope,
      $state,
      LoadingService,
      ProductService,
      ReasonsService,
    ) {
      this.$scope = $scope;
      this.$ngRedux = $ngRedux;
      this.$state = $state;
      this.LoadingService = LoadingService;
      this.ProductService = ProductService;
      this.ReasonsService = ReasonsService;
    }

    $onInit() {
      this.setStateConnection();

      if (this.ProductService.isAvailable('PATIENT_FORMS') && this.reason && this.reason.form && this.reason.form.questionsCount) {
        const answers = this.$ngRedux.getState().reasonForm.answers;

        // If reason has a form and there is no answer registered, go back to form page
        if (!!answers && !Object.keys(answers).length) {
          this.$state.go('patients.id.reasons.id.form');
        }
      } else {
        this.LoadingService.hide();
      }

      // If reason doesn't allow to set preferences, redirect to availabilities page
      if (this.reason && !this.ReasonsService.hasPreferences(this.reason)) {
        if (this.reason.allowConsecutiveAppointments && this.authenticatedPatient.children.length) {
          this.$state.go('patients.id.reasons.id.patients', { reasonId: this.reason.id });
        } else {
          this.$state.go('patients.id.reasons.id.availabilities');
        }
      }

      // When the date of last appointment and days before appointment are known, find the soonest possible date
      this.models.availability.soonestPossibleDate = this.calculateSoonestPossibleDate();

      // Filter eligible accounts
      if (this.ProductService.isAvailable('PATIENT_AUTHORIZED_PROFESSIONALS') && this.reason.accountFilter === 'optional') {
        const subscription = this.patient.subscription;
        this.eligibleAccounts = this.reason.eligibleAccounts.filter((eligibleAccount) => {
          if (subscription.account && subscription.account.id === eligibleAccount.account.id) {
            return true;
          }
          return !!subscription.authorizedAccounts.find(authorizedAccount => authorizedAccount.id === eligibleAccount.account.id);
        });
      } else {
        this.eligibleAccounts = this.reason.eligibleAccounts;
      }

      this.LoadingService.hide();
    }

    setStateConnection() {
      this.$scope.$on('$destroy', this.$ngRedux.connect(state => ({
        authenticatedPatient: authenticatedPatientSelector(state),
        group: state.group,
        reason: state.reasons.current,
        patient: currentPatientSelector(state),
        reasonForm: state.reasonForm,
      }))(this));
    }

    getPreferences() {
      const preferences = [];

      if (this.appointmentMomentPreferences.momentOfDay.morning) preferences.push('am');
      if (this.appointmentMomentPreferences.momentOfDay.afternoon) preferences.push('pm');
      if (this.appointmentMomentPreferences.momentOfDay.evening) preferences.push('eve');

      return preferences.length ? preferences : null;
    }

    getEligibleAccountsIds() {
      if (_.filter(this.models.availability.eligibleAccounts, selected => selected).length === 0 && this.ProductService.isAvailable('PATIENT_AUTHORIZED_PROFESSIONALS')) {
        return this.eligibleAccounts.map(eligibleAccount => eligibleAccount.account.id);
      }
      return Object.keys(this.models.availability.eligibleAccounts).filter(key => this.models.availability.eligibleAccounts[key]);
    }

    updateState() {
      if (this.models.note && (this.models.choice === 'other' || this.reason.visitReasonType === 'free')) {
        this.$ngRedux.dispatch(new ReasonsActions.UpdateReasonVisitChoice(this.models.note));
      } else if (this.models.choice) {
        this.$ngRedux.dispatch(new ReasonsActions.UpdateReasonVisitChoice(this.models.choice));
      }

      if (this.reason.allowTimePreference) {
        if (this.appointmentMomentPreferences.date !== null && this.appointmentMomentPreferences.type === 'period') {
          this.$ngRedux.dispatch(new ReasonsActions.UpdateReasonPreferredDate(this.appointmentMomentPreferences.date));
        }
        if (this.models.availability.soonestPossibleDate) {
          this.$ngRedux.dispatch(new ReasonsActions.UpdateReasonStartDate(this.models.availability.soonestPossibleDate.format('YYYY-MM-DD')));
        }

        this.$ngRedux.dispatch(new ReasonsActions.UpdateReasonPreference(this.getPreferences()));
      }

      if (this.reason.accountFilter === 'optional' && (this.reason.allowSelectHcp || this.ProductService.isAvailable('PATIENT_AUTHORIZED_PROFESSIONALS'))) {
        this.$ngRedux.dispatch(new ReasonsActions.UpdateEligibleAccountsIds(this.getEligibleAccountsIds()));
      }
    }

    goToReasonsPage() {
      this.$ngRedux.dispatch(new ReasonsActions.UnsetCurrentReason());
      this.$state.go('patients.id.reasons');
    }

    goToAvailabilities() {
      this.updateState();

      this.$state.go('patients.id.reasons.id.availabilities');
    }

    calculateSoonestPossibleDate() {
      const formQuestions = this.reasonForm.questions;
      const formAnswers = this.reasonForm.answers;

      if (formQuestions && formAnswers) {
        const question = formQuestions.find(item => item.kind === 'date_of_last_appointment');
        if (question) {
          const answer = formAnswers.find(item => item.questionId === question.id);
          if (answer) {
            return moment(answer.date).add(question.daysBeforeAppointment, 'days');
          }
        }
      }
      return this.models.availability.soonestPossibleDate;
    }

    get canContinue() {
      if (!this.reason) {
        return false;
      }

      if (this.reason.visitReasonType === 'choices' && this.models.choice === '') {
        return false;
      } else if (this.reason.visitReasonType === 'free' && this.models.note === '') {
        return false;
      } else if (this.reason.visitReasonType === 'both' && (this.models.choice === '' || (this.models.choice === 'other' && this.models.note === ''))) {
        return false;
      } else if (this.reason.prohibitedMessage) {
        return false;
      }

      return true;
    }
  },
  template: `
    <section class="booking-page">
      <div class="content">
        <reasons-navigation [reason]="$ctrl.reason"></reasons-navigation>

        <div class="wrapper">
          <div class="contextual-notice -has-icon" ng-if="$ctrl.reason && $ctrl.reason.prohibitedMessage">
            <div class="icon -warning"></div>
            <p>{{ $ctrl.reason.prohibitedMessage }}</p>
          </div>

          <reason-select-hcp reason="$ctrl.reason" eligible-accounts="$ctrl.eligibleAccounts" availability="$ctrl.models.availability" ng-if="$ctrl.reason.allowSelectHcp && $ctrl.eligibleAccounts.length > 0"></reason-select-hcp>
          <reason-visit-choices reason="$ctrl.reason" choice="$ctrl.models.choice" note="$ctrl.models.note"></reason-visit-choices>
          <appointment-moment-preferences
            [allow-consecutive-appointments]="$ctrl.reason.allowConsecutiveAppointments"
            [title-id]="'preferences_page.titles.availabilities'" 
            [preferences]="$ctrl.appointmentMomentPreferences" 
            [soonest-possible-date]="$ctrl.models.availability.soonestPossibleDate" 
            ng-if="$ctrl.reason.allowTimePreference">
          </appointment-moment-preferences>
          <div class="actions">
            <button class="hollow-button" ng-click="$ctrl.goToReasonsPage()" translate="actions.btn_cancel"></button>
            <button class="button -next" ng-disabled="!$ctrl.canContinue" ng-click="$ctrl.goToAvailabilities($ctrl.reason)">
              {{'choose_reason.next_link_text' | translate}}
            </button>
          </div>
        </div>
      </div>
    </section>
  `,
};



// WEBPACK FOOTER //
// ./main/components/reasons/reason-preferences.js